<template>
    <div class="phone-verification-page-result">
        <div class="phone-verification-page-result__content">
            <logos-container-ui center />
            <phone-verification-skeleton v-if="loading" />
            <div v-else>
                <div v-if="hasError">
                    <p class="phone-verification-status__title">
                        {{  $t('Verification Status') }}
                    </p>
                    <i class="phone-verification-page-result__error fas fa-times-circle" />
                    <p class="phone-verification-page-result__description">
                        {{ $t('There was an error during your phone verification. Please try again or use another verification method.') }}
                    </p>
                    <ui-button variant="primary" class="m-2" :loading="loading" @click="redirectToPhoneVerification">
                        <span class="phone-verification-page-result__other-method-button-text">
                            {{ $t('Try with another method') }}
                        </span>
                        <span class="phone-verification-page-result__other-method-arrow fas fa-arrow-right"></span>
                    </ui-button>
                    <ui-button :loading="loading" class="mb-1" variant="secondary" @click="handleSubmit">
                        {{ $t('Scan the QR code again.') }}
                    </ui-button>
                </div>
                <div v-if="same && status === 'success'">
                    <i class="phone-verification-status__icon fas fa-check-circle" />
                    <p class="phone-verification-status__title">{{ $t('Phone number verified') }}</p>
                    <p class="phone-verification-status__description">{{ $t('You can now use your account') }}</p>
                    <p class="phone-verification-status__description">
                        <span class="fa fa-spinner fa-spin" />
                        {{ $t('You will be redirected to the login page in a few seconds') }}
                    </p>
                </div>
                <div v-if="status === 'success' && !same && !showPhoneVerification">
                    <i class="phone-verification-status__icon fas fa-check-circle" />
                    <p class="phone-verification-status__title">{{ $t('Phone number verified') }}</p>
                    <p class="phone-verification-status__description">
                         {{ $t(`We’ve verified your account with the number`) }} <strong> {{ getPhone(phone_verified, country) }} </strong> <br>
                         {{ $t(`Currently, your registered number is`) }} <strong> {{ getPhone(user?.phone, user?.country) }} </strong> <br>
                         {{ $t(`Would you like to update your registered number to match the one you just verified?`) }}
                    </p>
                    <ui-button @click="show" class="m-2" :loading="loading" :disabled="loading" :loading-text="$t('Redirecting...')" variant="secondary">
                        {{ $t('No, try again') }}
                    </ui-button>
                    <ui-button @click="confirmation" class="m-2" :loading="update" :disabled="update" :loading-text="$t('Redirecting...')">
                        {{ $t('Yes, update my number') }}
                    </ui-button>
                </div>
                <div v-else-if="showPhoneVerification">
                    <phone-verification-status :status="status" :site_id="site_id" :phone="getPhone(user.phone, user.country)" :country="country" />
                </div>
                <p class="phone-verification-page-result__support-message mt-4">
                    {{ $t('If you are still having problems with login, start a support chat') }}.
                </p>
            </div>
        </div>
        <vue-recaptcha
        class="w-100"
        ref="recaptchaComponent"
        size="invisible"
        loadRecaptchaScript
        :sitekey="CONST.CAPTCHACODE"
        @verify="OTPInverse"
        />
    </div>
</template>

<script>
import parsePhoneNumber from 'libphonenumber-js'
import UiButton from '@/components/ui/buttons/Button.vue';
import PhoneVerificationSkeleton from '@/components/ui/skeletons/PhoneVerificationSkeleton.vue';
import LogosContainerUi from '@/components/ui/LogosContainer.vue'
import PhoneVerificationStatus from '@/components/verifications/PhoneVerificationStatus.vue';
import VueRecaptcha from 'vue-recaptcha';
import recaptchaUtils from '@/utils/recaptcha.utils';

export default {
    name: 'phone-verification-result',
    components: {
        UiButton,
        PhoneVerificationSkeleton,
        LogosContainerUi,
        PhoneVerificationStatus,
        VueRecaptcha
    },
    data() {
        return {
            sessionUuid: this.$route.query.sessionUuid,
            status: 'pending',
            loading: false,
            site_id: null,
            phone_verified: null,
            country: '',
            showPhoneVerification: false,
            user: null,
            update: false,
            same: false,
            hasError: false
        }
    },
    mounted() {
        setTimeout(() => {
            this.loadingData();
        }, 1000);
    },
    methods: {
        async loadingData() {
            try {
                this.loading = true;
                await this.getAccountDetails();
                await this.getResult();
            } catch (error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        async getAccountDetails() {
            try {
                this.loadingAccount = true;
                const { data } = await this.axiosAccount.get('/api/accounts/me');
                this.user = data;
            } catch(error) {
                this.showError(error);
            } finally {
                this.loadingAccount = false;
            }
        },
        async getResult() {
            try {
                this.loading = true;
                const { data } = await this.axiosAccount.get(`/api/otp-inverse/${this.sessionUuid}`);
                const { status, verifiedNumber, site_id, same, redirect_url } = data;
                if (same) {
                    this.same = same;
                    this.redirecting(redirect_url);
                }
                if (verifiedNumber) {
                    const { phoneNumber, countryCode } = verifiedNumber;
                    this.phone_verified = phoneNumber;
                    this.country = countryCode.countryIsoCode3166_2;
                }
                this.status = status;
                this.site_id = site_id;
            } catch (error) {
                this.hasError = true;
            } finally {
                this.loading = false;
            }
        },
        confirmation() {
            if (this.status === 'success') {
                this.updatePhoneNumber();
            }
        },
        show() {
           try {
                this.loading = true;
                this.showPhoneVerification = true; 
           } catch (error) {
                this.showError(error);
           } finally {
                this.loading = false;
           }
        },
        getPhone(phone, country) {
            const formattedPhoneNumber = parsePhoneNumber(phone, country);
            return formattedPhoneNumber.formatInternational()
        },
        redirecting(redirect_url) {
            if (!redirect_url) {
                this.$router.push('/login');
            } else {
                setTimeout(() => {
                    redirect_url.startsWith('/') ? this.$router.push(redirect_url) : window.location.replace(redirect_url);
                }, 5000);
            }
        },
        async updatePhoneNumber() {
            try {
                this.update = true;
                const payload = { phone: this.phone_verified, country_code: this.country };
                const { data } = await this.axiosAccount.put(`/api/otp-inverse/${this.sessionUuid}`, payload);
                const { redirect_url } = data;

                if (!redirect_url) {
                    this.redirectToLogin();
                    return;
                }
                if (redirect_url) {
                    this.redirecting(redirect_url);
                }
            } catch (error) {
                this.showError(error);
            } finally {
                this.update = false;
            }
        },
        redirectToPhoneVerification() {
            setTimeout(() => {
                this.loading = false;
                if (this.site_id && this.redirect_url) {
                    return this.$router.push('/phone-verification?site_id=' + this.site_id + '&redirect_url=' + this.redirect_url);
                }
                return this.$router.push('/phone-verification');
            }, 1000);
            this.loading = true;
        },
        async handleSubmit() {
            if (!recaptchaUtils.isAvailable()) {
                this.OTPInverse('');
                return;
            }

            this.$refs.recaptchaComponent.reset();
            this.$refs.recaptchaComponent.execute();
        },
        async OTPInverse(token) {
            try {
                this.loading = true;
                const payload = {
                    type: 'otp-inverse',
                    action: 'phone_verification',
                    from: 'Tendencys',
                    complements: {
                        site_id: this.site_id,
                        redirect_url: this.redirect_url,
                    },
                    token,
                };
                const { data } = await this.axiosAccount.post('/api/otp-inverse', payload);
                const { redirectionUrl } = data;
                if (redirectionUrl) {
                    redirectionUrl.startsWith('/') ? this.$router.push(redirectionUrl) : window.location.replace(redirectionUrl);
                }

            } catch (error) {
                if (error?.response?.status === 404) {
                    this.redirectToLogin();
                    return;
                }

                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        redirectToLogin() {
            this.$toasted.global.successMessage(this.$t('The phone number has been verified, please login again'));
            setTimeout(() => {
                this.$router.push('/login');
            }, 3000);
        }
    }
};
</script>

<style lang="scss">
.phone-verification-page-result {
    text-align: center;
    position: relative;

    &__back {
        position: absolute;
        left: 0;
        top: 0;
    }

    &__content {
        max-width: 650px;
        margin-left: auto;
        margin-right: auto;
    }

    &__logo {
        margin-bottom: 35px;
    }

    &__support-message {
        color: #c4c4c4;
        font-size: 0.875rem;
        font-weight: 400;
        margin-bottom: 0;
    }

    &__img {
        max-width: 120px;
        width: 100%;
    }
    
    &__title {
        font-size: 2.25rem;
        font-weight: 700;
        margin-bottom: 20px;
    }
    
    &__subtitle {
        font-size: 1.5rem;
        font-weight: 550;
        margin-bottom: 20px;
    }
    
    &__description {
        font-size: 1rem;
        margin-top: 1rem;
    }
    
    &__error {
        font-size: 3rem;
        color: #FF0000;
    }
    
    &__update-phone {
        color: #9093A1;
        font-size: 16px;
        font-weight: 400;
        margin-bottom: 24px;
    }
    
    &__link {
        color: $accounts-secondary !important;
        font-weight: 600 !important;
        font-size: 16px !important;
        text-decoration: none;
    }
    
    &__other-method-button {
        align-items: center !important;
        display: flex !important;
        height: 46px;
        padding-left: 8px !important;
        padding-right: 8px !important;
        margin-bottom: 0 !important;
        max-width: 100%;
        width: 100%;
        
        @media(min-width: 768px) {
            width: auto;
        }
    }
    
    &__other-method-button-text {
        line-height: 130%;
        overflow: hidden;
        text-overflow: ellipsis;
        width: auto;
        white-space: nowrap;
    }
    
    &__other-method-arrow {
        font-size: 14px;
        margin-left: auto;
        margin-top: -2px;
    }
}
</style>